import React, { useState } from "react";
import { Edit, Trash, Mail, Calendar, Clock, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Permissions } from "../../../../permissions";
import EditUserModal from "./EditUserModal";

const UserCard = ({ user, onDelete, onEdit }) => {
  const navigate = useNavigate();
  const canEditUser = Permissions.includes("edit_employee");
  const canDeleteUser = Permissions.includes("delete_employee");
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Generate initials for avatar
  const getInitials = name => {
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase();
  };

  // Format date to readable format
  const formatDate = dateString => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getFormattedDate = date => {
    const month = date.toLocaleString("default", { month: "short" });
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  };

  const handleEditUser = () => {
    setEditModalOpen(true);
  };

  // Professional pastel colors for avatars
  const getAvatarColor = () => {
    const colors = [
      "#92A8D1", // Soft blue
      "#B5D8CC", // Mint green
      "#EAC4D5", // Soft pink
      "#D5E4EB", // Light blue
      "#D6CADD", // Lavender
      "#CAE1FF", // Periwinkle
      "#FFD8B1", // Peach
      "#AEC9EB", // Steel blue
    ];

    if (!user.name) return colors[0];

    // Simple hash function to get consistent color based on name
    const hash = user.name.split("").reduce((acc, char) => {
      return acc + char.charCodeAt(0);
    }, 0);

    return colors[hash % colors.length];
  };

  return (
    <>
      <div className="bg-white border border-[#E5E7EB] rounded-lg shadow-sm overflow-hidden p-5">
        <div className="flex items-center justify-between">
          {/* Avatar with Initials */}
          <div
            style={{
              backgroundColor: getAvatarColor(),
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#4B5563",
              fontFamily: "Outfit",
              fontWeight: "500",
              fontSize: "18px",
            }}>
            {user.profileImage ? (
              <img
                src={user.profileImage}
                alt={user.name}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              getInitials(user.name)
            )}
          </div>

          {/* Edit Button */}
          <button
            onClick={handleEditUser}
            className="text-[#5652B0] hover:text-[#4A47A3]">
            <Edit size={20} />
          </button>
        </div>

        {/* User Name & Email */}
        <div className="mt-4">
          <h3 className="text-lg font-medium text-[#374151] font-outfit">
            {user.name}
          </h3>
          <div className="text-sm text-[#6B7280] font-outfit flex items-center mt-1">
            <Mail size={14} className="mr-1" />
            {user.email}
          </div>
          {user.phone && (
            <div className="text-sm text-[#6B7280] font-outfit flex items-center mt-1">
              <Phone size={14} className="mr-1" />
              {user.phone}
            </div>
          )}
        </div>

        {/* Role & Status Pills */}
        <div className="mt-4 flex flex-wrap gap-2">
          <span
            className={`text-xs px-3 py-1 rounded-full font-medium bg-[rgba(86,82,176,0.1)] text-[#5652B0]`}>
            {user.role}
          </span>
          <span
            className={`text-xs px-3 py-1 rounded-full font-medium ${
              user.status === "active"
                ? "bg-[rgba(34,197,94,0.1)] text-[#22c55e]"
                : "bg-[rgba(107,114,128,0.1)] text-[#6B7280]"
            }`}>
            {user.status === "active" ? "Active" : "Inactive"}
          </span>
        </div>

        {/* Created Date & Last Login */}
        <div className="mt-4 pt-4 border-t border-[#E5E7EB] grid grid-cols-2 gap-2">
          <div className="text-xs text-gray-500 font-outfit">
            <div className="font-medium mb-1">Created:</div>
            <div className="flex items-center">
              <Calendar size={12} className="mr-1" />
              <span>
                {new Date(user.createdAt).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>
          <div className="text-xs text-gray-500 font-outfit">
            <div className="font-medium mb-1">Last login:</div>
            <div className="flex items-center">
              <Clock size={12} className="mr-1" />
              <span>
                {new Date(user.lastLogin).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Edit User Modal */}
      <EditUserModal
        open={editModalOpen}
        handleClose={() => setEditModalOpen(false)}
        onSave={onEdit}
        user={user}
      />
    </>
  );
};

export default UserCard;
